import { window as $window } from "global";
import { isVisible } from "../../../util";
import { CxpVerbs, Events, VerbEventPayload, WaCxpItem } from "./item";

// Global Namespace: Cengage.WA.Question.CXP.Frame

// The WaCxpFrame class manages the iframe that contains CXP question content.

// The WaCxpFrame class:
// * bubbles up messages from the iframe to the parent page's WebAssign JavaScript
// * handles resizing the iframe and alerts any grandparent pages to resize (e.g. Assignment or Question page is nested in a iframe)

// Notes: For every CXP iframe, there should be a corresponding WaCxpFrame instance that has been registered (a 1-to-1 relationship). The CXP iframe is decoupled
// and is unaware that is being "managed" by the WaCxpFrame instance.

export interface BindRequestEvent extends CustomEvent {
    detail: {
        callback: (waCxpFrame: WaCxpFrame) => void;
        onCxpMultiItemFullyAnswered: () => void;
        onCxpMultiItemGrade: (answer: string) => void;
        onCxpNowVisible: () => void;
        onCxpReady: () => void;
        onCxpTouched: (type: string) => void;
    };
}

export class WaCxpFrame {
    cssId: string;
    delegatedInit: boolean | undefined;
    private element: HTMLIFrameElement | undefined;
    private onCxpMultiItemFullyAnswered: (() => void) | undefined;
    private onCxpMultiItemGrade: ((answer: string) => void) | undefined;
    private onCxpNowVisible: (() => void) | undefined;
    private onCxpReady: (() => void) | undefined;
    private onCxpTouched: ((type: string) => void) | undefined;
    private static registeredFrames: Map<string, WaCxpFrame> = new Map();
    private waCxpItem: WaCxpItem | undefined;

    public static readonly EVENT_TYPE_BIND: string = "bindCxpFrameRequest";
    public static readonly EVENT_TYPE_FRAME_REGISTERED: string =
        "frameRegistered";

    private constructor(cssId: string, delegatedInit?: boolean) {
        this.cssId = cssId;

        if (delegatedInit !== undefined) {
            this.delegatedInit = delegatedInit;
        } else {
            // check for a JavaScript global that indicates this a student take
            if ($window.CengageWebAssignAssignmentIsStudentTake) {
                this.delegatedInit = true;
            } else {
                this.delegatedInit = false;
            }
        }

        const element = $window.document.getElementById(
            this.cssId,
        ) as HTMLIFrameElement;

        if (!element) {
            console.warn(
                `Expected to find an iframe with id '${this.cssId}', but no iframe was found.`,
            );
            return;
        }

        this.element = element;

        if (this.element.tagName != "IFRAME") {
            console.warn(
                `Expected to find an iframe with id '${this.cssId}', but the element is NOT an iframe. Found a '${this.element.tagName}' instead.`,
            );
            return;
        }

        // register a post message listener for events from the iframe
        // the post message listener is required to be registered at the top-level window scope
        // therefore there will be 1 listener per iframe that receives messages from all iframes on the page
        $window.addEventListener(
            "message",
            (event: MessageEvent) => this.handlePostMessage(event),
            false,
        );

        if (isVisible(this.element)) {
            if (!this.delegatedInit) {
                // switch iframe source to frame.html
                this.element.src = `${__PATH__}frame${__VERSION__}.html`;
            } else {
                // the WebAssign question will be the one to resume the init process (delegated init)
                // the default page is a loading page, so init is basically paused at this point
            }
        } else {
            // switch iframe source to frame_preload.html
            this.element.src = `${__PATH__}frame_preload${__VERSION__}.html`;

            // Assumption: the expected pattern, if the frame is hidden at all, it is hidden on page load.
            // If the frame is hidden, there is some interaction some undetermined time after page load that
            // unhides the frame. Once the frame is unhidden the 1st time, there is some CXP initiation. Future
            // hide/unhide events are ignored.
            const resizeObserver = new ResizeObserver(() => {
                if (this.element && isVisible(this.element)) {
                    // stop checking for visibility changes
                    resizeObserver.disconnect();
                    this.onCxpNowVisible?.();

                    // switch iframe source to frame.html
                    this.element.src = `${__PATH__}frame${__VERSION__}.html`;
                }
            });

            resizeObserver.observe(this.element);
        }
    }

    private addBindRequestEventListener() {
        // register an event listener for questions to initiate a connection to this frame manager
        this.element?.addEventListener(
            WaCxpFrame.EVENT_TYPE_BIND,
            (event) => this.handleBindRequest(event as BindRequestEvent),
            false,
        );
    }

    // called by WebAssign question
    getAnswerForBox(boxNum: number) {
        // assert the CXP code has finished initializing
        if (!this.waCxpItem) return "";

        return this.waCxpItem.getAnswerForBox(boxNum);
    }

    static getCxpFrameById(cssId: string) {
        return WaCxpFrame.registeredFrames.get(cssId);
    }

    // called by WebAssign question
    getBoxData() {
        // assert the CXP code has finished initializing
        if (!this.waCxpItem) return 0;

        return this.waCxpItem.getBoxData();
    }

    private handleBindRequest(event: BindRequestEvent) {
        if (!this.element) return;
        // a WebAssign question has requested to bind to this CXP frame

        // register the WebAssign Question callbacks
        this.onCxpMultiItemFullyAnswered =
            event.detail.onCxpMultiItemFullyAnswered;
        this.onCxpMultiItemGrade = event.detail.onCxpMultiItemGrade;
        this.onCxpNowVisible = event.detail.onCxpNowVisible;
        this.onCxpReady = event.detail.onCxpReady;
        this.onCxpTouched = event.detail.onCxpTouched;

        // bind to the WebAssign Question
        event.detail.callback(this);

        if (isVisible(this.element)) {
            this.onCxpNowVisible?.();
        }
    }

    // Post Messaging Notes:
    // * filter out messages that are Cross Origin
    // * the post messages should have target ids so that we can filter out messages not targeted at this iframe
    // * the messages that bubble up from the iframe are converted to the registered callbacks (e.g. onCxpReady, onCxpTouched, ...)
    private handlePostMessage(event: MessageEvent) {
        // filter out CORS messages
        if (event.origin !== $window.location.origin) {
            console.warn(
                `Rejected CORS message. (${event.origin} !== ${$window.location.origin})`,
            );
            return;
        }

        // filter out messages that aren't targeted at this frame
        let message = event.data;
        if (message.targetId != this.cssId) {
            return;
        }

        switch (message.type) {
            case Events.Ready: {
                const waCxpItem = this.element?.contentWindow?.waCxpItem;
                if (!waCxpItem) {
                    // TODO: is this really possible?
                    console.warn(
                        `Unable to find global waCxpItem object in the iframe '${this.cssId}'`,
                    );
                    return;
                }
                this.waCxpItem = waCxpItem;

                this.onCxpReady?.();

                break;
            }
            case Events.Resize:
                this.resize(message.payload.height, message.payload.width);
                break;

            case Events.Touched:
                this.onCxpTouched?.(message.payload.type);

                break;

            case Events.Verb: {
                const payload = message.payload as VerbEventPayload;

                switch (payload.verb) {
                    case CxpVerbs.Regrade:
                        this.onCxpMultiItemGrade?.(payload.data);

                        break;

                    case CxpVerbs.StatusUpdate:
                        this.onCxpMultiItemFullyAnswered?.();
                        break;
                }
                break;
            }
        }
    }

    // called by WebAssign question
    isMultiItem() {
        // assert the CXP code has finished initializing
        if (!this.waCxpItem) return false;

        return this.waCxpItem.isMultiItem;
    }

    // the register function is the main entry point
    static register(cssId: string, delegatedInit?: boolean) {
        let cxpItemFrame = WaCxpFrame.getCxpFrameById(cssId);

        if (cxpItemFrame && cxpItemFrame instanceof WaCxpFrame) {
            // return the already registered WaCxpFrame instance
            return cxpItemFrame;
        }
        cxpItemFrame = new WaCxpFrame(cssId, delegatedInit);

        // add an event listener for a bind request from a WebAssign question
        cxpItemFrame.addBindRequestEventListener();
        WaCxpFrame.setCxpFrameById(cssId, cxpItemFrame);

        if (cxpItemFrame.element) {
            // set a flag that the frame is registered that WebAssign questions can check for
            // also fire a registered event if a WebAssign question is listening for a callback
            cxpItemFrame.element.setAttribute("data-registered", "true");

            const event = new CustomEvent(
                WaCxpFrame.EVENT_TYPE_FRAME_REGISTERED,
                {
                    bubbles: false,
                },
            );
            cxpItemFrame.element.dispatchEvent(event);
        }

        return cxpItemFrame;
    }

    resize(height: number, _width: number) {
        if (!this.element) return;

        if (Math.abs(height - this.element.offsetHeight) > 0) {
            this.element.style.height = `${height}px`;
        }

        // check if we are in a frame (i.e. nested iframes)
        if ($window.frameElement) {
            // we assume the parent is listening for a load event to resize
            // retrigger the load event for parent to resize
            const event = new CustomEvent("load", {
                bubbles: false,
            });
            $window.frameElement.dispatchEvent(event);
        }
    }

    // called by WebAssign question
    resumeInit() {
        if (!this.element) return;

        // if the iframe is visible, resume init
        // if the iframe is hidden, then the resizeObserver in the constructor will take care of resume init
        if (isVisible(this.element)) {
            // switch iframe source to frame.html
            this.element.src = `${__PATH__}frame${__VERSION__}.html`;
        }
    }

    static setCxpFrameById(cssId: string, waCxpFrame: WaCxpFrame) {
        WaCxpFrame.registeredFrames.set(cssId, waCxpFrame);
    }
}
